import { useEffect, useMemo, useState } from "react";
import { GiFarmTractor } from "react-icons/gi";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioFabricanteMaquina, repositorioTipoMaquina } from "../../sync/repositories/fazendas";
import { extrairMensagensErro } from "../../utils/errorUtils";

const AJUDA = {
  titulo: "Fabricantes de Máquinas",
  descricao:
    "Cadastre as marcas/fabricantes de máquinas usados na sua operação (John Deere, Massey Ferguson, Case IH, etc.) e associe cada um aos Tipos de Máquinas que ele produz. Esse cadastro é usado na Gestão de Máquinas.",
  secoes: [
    {
      titulo: "Cadastro de Fabricante",
      campos: [
        { nome: "Nome", obrigatorio: true, explicacao: "Nome do fabricante (ex.: John Deere). Não pode ser duplicado." },
        { nome: "Tipos de Máquinas", obrigatorio: false, explicacao: "Relacione o fabricante aos Tipos de Máquinas que ele produz. Um fabricante pode produzir vários tipos (ex.: John Deere fabrica Tratores e Colheitadeiras)." },
        { nome: "Status", obrigatorio: true, explicacao: "Fabricantes inativos não aparecem nos selects de novos cadastros, mas continuam disponíveis para registros antigos." },
      ],
    },
  ],
  dicas: [
    "Um mesmo Fabricante pode estar associado a vários Tipos de Máquinas — use o checklist para marcar todos que se aplicam.",
    "Na Gestão de Máquinas, ao escolher um Tipo, o select de Fabricante mostra apenas os fabricantes associados a ele — mantenha essa relação atualizada.",
  ],
};

export default function GestaoFabricantesMaquinas() {
  const [fabricantes, setFabricantes] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtros, setFiltros] = useState({ busca: "", status: "todos" });
  const [editando, setEditando] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [formData, setFormData] = useState({ nome: "", tipos_maquina: [], ativo: true });
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  const carregar = async () => {
    try {
      setCarregando(true);
      const [rFab, rTip] = await Promise.all([
        repositorioFabricanteMaquina.listar(),
        repositorioTipoMaquina.listar(),
      ]);
      setFabricantes(rFab.data.results || rFab.data);
      setTipos(rTip.data.results || rTip.data);
    } catch (e) {
      console.error(e);
      alert("Erro ao carregar fabricantes.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const tiposOrdenados = useMemo(
    () => [...tipos].sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR")),
    [tipos]
  );

  const fabricantesFiltrados = fabricantes.filter((f) => {
    if (filtros.status === "ativos" && !f.ativo) return false;
    if (filtros.status === "inativos" && f.ativo) return false;
    if (filtros.busca) {
      const b = filtros.busca.toLowerCase();
      return (f.nome || "").toLowerCase().includes(b);
    }
    return true;
  });

  const handleNovo = () => {
    setEditando(null);
    setFormData({ nome: "", tipos_maquina: [], ativo: true });
    setErros([]);
    setMostrarFormulario(true);
  };

  const handleEditar = (f) => {
    setEditando(f);
    setFormData({
      nome: f.nome || "",
      tipos_maquina: Array.isArray(f.tipos_maquina) ? f.tipos_maquina : [],
      ativo: f.ativo !== false,
    });
    setErros([]);
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setMostrarFormulario(false);
    setEditando(null);
    setFormData({ nome: "", tipos_maquina: [], ativo: true });
    setErros([]);
  };

  const toggleTipo = (id) => {
    setFormData((prev) => {
      const lista = prev.tipos_maquina.includes(id)
        ? prev.tipos_maquina.filter((t) => t !== id)
        : [...prev.tipos_maquina, id];
      return { ...prev, tipos_maquina: lista };
    });
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) { setErros(["O nome é obrigatório."]); return; }
    setSalvando(true);
    setErros([]);
    try {
      const payload = {
        nome: formData.nome.trim(),
        ativo: formData.ativo,
        tipos_maquina_ids: formData.tipos_maquina,
      };
      if (editando) {
        await repositorioFabricanteMaquina.atualizar(editando.id, payload);
        alert("Fabricante atualizado com sucesso!");
      } else {
        await repositorioFabricanteMaquina.criar(payload);
        alert("Fabricante cadastrado com sucesso!");
      }
      handleCancel();
      carregar();
    } catch (e2) {
      setErros(extrairMensagensErro(e2));
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (f) => {
    const msg = "Tem certeza que deseja excluir este fabricante? Se houver máquinas vinculadas, ele será apenas inativado.";
    if (!window.confirm(msg)) return;
    try {
      await repositorioFabricanteMaquina.excluir(f.id);
      alert("Operação concluída.");
      carregar();
    } catch (e) {
      console.error(e);
      alert("Erro ao excluir fabricante.");
    }
  };

  const tiposNomes = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return "—";
    return ids
      .map((id) => tipos.find((t) => t.id === id)?.nome)
      .filter(Boolean)
      .join(", ") || "—";
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              Fabricantes de Máquinas <GiFarmTractor size={32} />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button onClick={handleNovo} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 shadow-md">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Fabricante
            </button>
          )}
        </div>

        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-xl font-semibold mb-4">{editando ? "Editar Fabricante" : "Cadastrar Novo Fabricante"}</h3>
            <form onSubmit={handleSalvar} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  required disabled={salvando}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipos de Máquinas relacionados</label>
                {tiposOrdenados.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum tipo de máquina cadastrado. Cadastre primeiro na tela "Tipos de Máquinas".</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 max-h-64 overflow-y-auto border border-gray-200 rounded p-2">
                    {tiposOrdenados.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                        <input
                          type="checkbox"
                          checked={formData.tipos_maquina.includes(t.id)}
                          onChange={() => toggleTipo(t.id)}
                          disabled={salvando}
                          className="rounded text-green-600 focus:ring-green-500"
                        />
                        <span>{t.nome}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                    disabled={salvando}
                    className="rounded text-green-600 focus:ring-green-500"
                  />
                  Ativo
                </label>
              </div>

              {erros.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
                  {erros.map((m, i) => <div key={i}>{m}</div>)}
                </div>
              )}

              <div className="flex gap-3">
                <button type="submit" disabled={salvando} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded disabled:bg-blue-300">
                  {salvando ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                </button>
                <button type="button" onClick={handleCancel} disabled={salvando} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {!mostrarFormulario && (
          <>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Filtros</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                  <input
                    type="text"
                    value={filtros.busca}
                    onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
                    placeholder="Nome do fabricante..."
                    className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filtros.status}
                    onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                    className="w-full border border-green-500 p-2 rounded"
                  >
                    <option value="todos">Todos</option>
                    <option value="ativos">Apenas ativos</option>
                    <option value="inativos">Apenas inativos</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b bg-gray-50 text-gray-600">
                {fabricantesFiltrados.length} fabricante(s) encontrado(s)
              </div>
              {carregando ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipos de Máquinas</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fabricantesFiltrados.map((f) => (
                        <tr key={f.id} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="p-3 text-sm font-mono">{f.id}</td>
                          <td className="p-3 text-sm font-medium">{f.nome}</td>
                          <td className="p-3 text-sm">{tiposNomes(f.tipos_maquina)}</td>
                          <td className="p-3 text-sm">
                            {f.ativo !== false ? (
                              <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs">Ativo</span>
                            ) : (
                              <span className="px-2 py-1 rounded-full bg-gray-200 text-gray-700 text-xs">Inativo</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button onClick={() => handleEditar(f)} className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm">Editar</button>
                              <button onClick={() => handleExcluir(f)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">Excluir</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {fabricantesFiltrados.length === 0 && (
                        <tr><td colSpan="5" className="p-8 text-center text-gray-500">Nenhum fabricante encontrado.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
